import { Button, Dropdown, Flex, List, Modal, Popconfirm, Space, Table, Typography } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { DATE_FORMAT } from "common/constants";
import currency from "currency.js";
import {
  useDeletePaymentIntentMutation,
  useFetchPaymentIntentsQuery,
} from "features/consumerPortalUpcomingPayments/paymentIntentsAPI";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import useIsMobile from "hooks/useIsMobile";
import styled from "styled-components";

const StyledList = styled(List)`
  margin-top: 12px;
  border-radius: 8px;
  overflow: hidden;
  .ant-list-item {
    background: white;
    padding: 12px;
  }
  .ant-list-item-action {
    margin-inline-start: 8px !important;
  }
  .ant-list-item-action > li {
    padding: 0 !important;
  }
`;

export default function ConsumerPortalUpcomingPayments() {
  const { data: accounts = [], isLoading: isAccountsLoading } = useFetchPaymentIntentsQuery({
    scheduledDateStart: moment().add("day", 1).format(DATE_FORMAT),
  });
  const [deletePaymentIntent, { isLoading: isDeleteLoading }] = useDeletePaymentIntentMutation();
  const isMobile = useIsMobile();

  const onPaymentCancel = (uuid) => {
    deletePaymentIntent({ paymentIntentUuids: [uuid] });
  };

  const columns = [
    {
      title: "Scheduled Date",
      dataIndex: "scheduledDate",
      key: "scheduledDate",
      render: (_, record) =>
        record.scheduledDate && moment(record.scheduledDate).format(DATE_FORMAT),
    },
    {
      title: "Account ID(s)",
      dataIndex: "accountExternalIds",
      key: "accountExternalIds",
      render: (_, record) => (record.accountExternalIds ?? []).join(", "),
    },
    {
      title: "Payment Method",
      dataIndex: "method",
      key: "method",
      render: (_, record) => {
        if (record?.paymentMethod?.type === "process_card") {
          return `Credit Card ****${record?.paymentMethod.cardLast4Digits}`;
        }
        if (record?.paymentMethod?.type === "process_bank_account") {
          return `Bank Account ****${record?.paymentMethod.bankAccountLast4Digits}`;
        }

        return "-";
      },
    },
    {
      title: "Amount",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (_, record) => currency(record.totalAmount, { precision: 2 }).format(),
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      key: "action",
      render: (_, record) =>
        record.dataSource === "consumer_portal" ? (
          <Space size="middle">
            <Popconfirm
              title="Are you sure you want to cancel this payment?"
              onConfirm={() => onPaymentCancel(record.uuid)}
            >
              <Button type="link">Cancel</Button>
            </Popconfirm>
          </Space>
        ) : (
          <Link to="/contact">Contact Us</Link>
        ),
    },
  ];

  const filteredAccounts = accounts.filter(
    (account) =>
      moment(account.scheduledDate).isSameOrAfter(moment(), "day") && !account.executedAt,
  );

  return (
    <>
      <h3>Upcoming Payments</h3>
      {isMobile ? (
        <MobileUpcomingPaymentsView
          accounts={filteredAccounts}
          isLoading={isAccountsLoading || isDeleteLoading}
          onPaymentCancel={onPaymentCancel}
        />
      ) : (
        <Table
          scroll={{ x: "max-content" }}
          loading={isAccountsLoading || isDeleteLoading}
          dataSource={filteredAccounts}
          // @ts-ignore
          columns={columns}
        />
      )}
    </>
  );
}

function MobileUpcomingPaymentsView({ accounts, isLoading, onPaymentCancel }) {
  return (
    <StyledList
      itemLayout="horizontal"
      loading={isLoading}
      dataSource={accounts}
      pagination={{
        pageSize: 5,
        hideOnSinglePage: true,
      }}
      renderItem={(record) => {
        /** @type {any} */
        const item = record;
        const onConfirmCancel = () => onPaymentCancel(item.uuid);
        const handleMenuClick = ({ key }) => {
          if (key === "cancel") {
            Modal.confirm({
              title: "Are you sure you want to cancel this payment?",
              onOk: onConfirmCancel,
            });
          }
          if (key === "contact") {
            // use Link in menu item label, but as a fallback:
            window.location.assign("/contact");
          }
        };
        const menuItems =
          item.dataSource === "consumer_portal"
            ? [
                {
                  key: "cancel",
                  label: "Cancel",
                },
              ]
            : [
                {
                  key: "contact",
                  label: <Link to="/contact">Contact Us</Link>,
                },
              ];
        return (
          <List.Item
            key={item.uuid}
            actions={[
              <Dropdown
                key="actions"
                trigger={["click"]}
                menu={{ items: menuItems, onClick: handleMenuClick }}
              >
                <Button type="text" icon={<EllipsisOutlined />} />
              </Dropdown>,
            ]}
          >
            <Flex justify="space-between" align="flex-start" gap={16} flex={1}>
              <Flex vertical>
                <Typography.Text strong>
                  {item.scheduledDate && moment(item.scheduledDate).format(DATE_FORMAT)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {(item.accountExternalIds ?? []).join(", ") || "No Account IDs"}
                </Typography.Text>
              </Flex>
              <Flex vertical align="flex-end">
                <Typography.Text>
                  {(() => {
                    if (item?.paymentMethod?.type === "process_card") {
                      return `Credit Card ****${item?.paymentMethod.cardLast4Digits}`;
                    }
                    if (item?.paymentMethod?.type === "process_bank_account") {
                      return `Bank Account ****${item?.paymentMethod.bankAccountLast4Digits}`;
                    }
                    return "-";
                  })()}
                </Typography.Text>
                <Typography.Text strong>
                  {currency(item.totalAmount, { precision: 2 }).format()}
                </Typography.Text>
              </Flex>
            </Flex>
          </List.Item>
        );
      }}
    />
  );
}
